import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PIPELINE_STAGES } from "../data/content";

/* ═══════════════════════════════════════════════════════════════
   Pipeline — reframed as MILESTONES a producer signs off on.
   Each panel: department + what they receive + what they approve
   + how long. Not process — outcomes.
   ═══════════════════════════════════════════════════════════════ */
export default function Pipeline() {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-83%"]);
  const barW = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const activeIdx = useTransform(scrollYProgress, [0, 1], [0, PIPELINE_STAGES.length - 1]);

  return (
    <section
      id="pipeline"
      ref={container}
      className="relative"
      style={{ height: `${PIPELINE_STAGES.length * 80}vh` }}
      data-testid="vw-pipeline"
    >
      <div
        className="sticky top-0 h-screen w-full overflow-hidden bg-[var(--vw-bg)] isolate"
        style={{ willChange: "transform", contain: "paint" }}
      >
        {/* Background grid */}
        <div className="absolute inset-0 vw-grid opacity-30 pointer-events-none" />

        {/* Top header */}
        <div className="absolute top-0 left-0 right-0 z-20 vw-container pt-20 md:pt-24 pb-6">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="vw-eyebrow mb-4">SCREENPLAY TO DCP · 12 MILESTONES · ONE TEAM</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.92] tracking-tight max-w-[22ch]">
                Twelve milestones. Twelve producer sign-offs. <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">One P&amp;L</span>.
              </h2>
            </div>
            <motion.div
              className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] flex items-center gap-3 border border-[var(--vw-border-strong)] px-4 py-2"
            >
              <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
              <StageBadge activeIdx={activeIdx} />
              &nbsp;/&nbsp;{String(PIPELINE_STAGES.length).padStart(2, "0")}
            </motion.div>
          </div>
        </div>

        {/* Horizontal track */}
        <motion.div
          style={{ x }}
          className="absolute top-0 left-0 h-full flex items-center pt-40 md:pt-44 pb-28"
        >
          {PIPELINE_STAGES.map((s, i) => (
            <StagePanel key={s.key} stage={s} idx={i} />
          ))}
        </motion.div>

        {/* Bottom : milestone strip + progress bar */}
        <div className="absolute bottom-8 left-0 right-0 vw-container z-20 space-y-4">
          <MilestoneStrip activeIdx={activeIdx} />

          <div className="flex items-center gap-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">
              PROGRESS
            </span>
            <div className="flex-1 h-[2px] bg-[var(--vw-border)] relative overflow-hidden">
              <motion.div
                style={{ width: barW }}
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-[var(--vw-cyan)] to-[var(--vw-blue)]"
              />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] hidden sm:inline">
              SCROLL TO ADVANCE
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Single stage panel ─────────────────────────────────────── */
function StagePanel({ stage: s, idx: i }) {
  return (
    <div
      className="w-[92vw] md:w-[75vw] lg:w-[58vw] shrink-0 px-6 md:px-12"
      data-testid={`vw-pipeline-stage-${s.key}`}
    >
      <div className="relative">
        {/* Stage tag row */}
        <div className="flex items-baseline gap-6 mb-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--vw-cyan)]">
            STAGE {String(i + 1).padStart(2, "0")} / 12
          </span>
          <span className="h-px flex-1 bg-[var(--vw-border)]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">
            {s.duration}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-display text-[8vw] md:text-[6vw] lg:text-[4.6vw] leading-[0.92] tracking-tighter mb-4 text-[var(--vw-text)]">
          {s.title}
        </h3>

        {/* Body */}
        <p className="max-w-2xl text-base md:text-lg text-[var(--vw-text-dim)] leading-relaxed mb-6">
          {s.body}
        </p>

        {/* Milestone + Output — the producer-facing outcome */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
          <div className="border border-[var(--vw-cyan)]/40 bg-[color:rgba(0,229,255,0.05)] p-4">
            <div className="flex items-center gap-2 font-mono text-[9.5px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] mb-2">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              MILESTONE · PRODUCER SIGN-OFF
            </div>
            <div className="font-display text-[18px] md:text-[19px] leading-snug text-[var(--vw-text)] tracking-tight">
              {s.milestone}
            </div>
          </div>
          <div className="border border-[var(--vw-border)] bg-black/30 p-4">
            <div className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-2">
              YOU RECEIVE
            </div>
            <div className="font-mono text-[13px] text-[var(--vw-text)] leading-snug">
              {s.output}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Milestone strip — shows the whole chain as forward motion ── */
function MilestoneStrip({ activeIdx }) {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    return activeIdx.on("change", (v) =>
      setN(Math.max(0, Math.min(PIPELINE_STAGES.length - 1, Math.round(v))))
    );
  }, [activeIdx]);

  return (
    <div className="hidden md:flex items-center gap-1 overflow-hidden font-mono text-[9.5px] uppercase tracking-[0.22em]">
      {PIPELINE_STAGES.map((s, i) => {
        const isActive = i === n;
        const isPast = i < n;
        return (
          <React.Fragment key={s.key}>
            <div
              className={`flex items-center gap-1.5 py-1.5 px-2 border transition-colors ${
                isActive
                  ? "border-[var(--vw-cyan)] text-[var(--vw-cyan)] bg-[color:rgba(0,229,255,0.08)]"
                  : isPast
                  ? "border-[var(--vw-cyan)]/30 text-[var(--vw-cyan)]/60"
                  : "border-[var(--vw-border)] text-[var(--vw-text-mute)]"
              }`}
            >
              <span
                className={`w-1 h-1 ${
                  isActive
                    ? "bg-[var(--vw-cyan)]"
                    : isPast
                    ? "bg-[var(--vw-cyan)]/60"
                    : "bg-[var(--vw-text-mute)]/40"
                }`}
              />
              <span className="whitespace-nowrap">{s.title.split(/[·\s]/)[0]}</span>
            </div>
            {i < PIPELINE_STAGES.length - 1 && (
              <span className={`w-2 h-px shrink-0 ${isPast || isActive ? "bg-[var(--vw-cyan)]/50" : "bg-[var(--vw-border)]"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* Tiny live badge */
function StageBadge({ activeIdx }) {
  const [n, setN] = React.useState(1);
  React.useEffect(() => {
    return activeIdx.on("change", (v) =>
      setN(Math.max(1, Math.min(PIPELINE_STAGES.length, Math.round(v) + 1)))
    );
  }, [activeIdx]);
  return <span className="text-[var(--vw-text)]">STAGE {String(n).padStart(2, "0")}</span>;
}
